// src/Profile/redux/thunks/vitalThunks.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosMedical } from "../../../api/axios";
import ENDPOINTS from "../../../api/endpoints";

const unwrap = (payload) =>
  payload?.data ??
  payload?.vitals ??
  payload?.signals ??
  payload?.items ??
  payload?.results ??
  payload?.records ??
  payload;

const extractArray = (payload) => {
  const value = unwrap(payload);

  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.vitals)) return value.vitals;
  if (Array.isArray(value?.signals)) return value.signals;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value?.records)) return value.records;

  return null;
};

/**
 * GET toutes les mesures
 * payload: { userId, type? }
 */
export const fetchVitalSignals = createAsyncThunk(
  "vital-signs/fetchAll",
  async ({ userId, type }, { rejectWithValue }) => {
    try {
      const url = ENDPOINTS.MEDICAL.VITALS(userId);

      const { data } = await axiosMedical.get(url, {
        params: type ? { type } : undefined,
        headers: { Accept: "application/json" },
      });

      const list = extractArray(data);

      if (!Array.isArray(list)) {
        return rejectWithValue("Format inattendu: array attendu");
      }

      return { data: list, userId };
    } catch (err) {
      return rejectWithValue(
        err?.response?.data || err?.message || "fetchVitalSignals failed"
      );
    }
  }
);

/**
 * ADD
 * payload attendu:
 * {
 *   userId,
 *   values: {
 *     type,
 *     value,
 *     unit?,
 *     measuredAt?,
 *     customLabel?
 *   },
 *   clientId?
 * }
 */
export const addVitalSignal = createAsyncThunk(
  "vital-signs/add",
  async ({ userId, values, clientId }, { rejectWithValue }) => {
    try {
      const body = {
        type: values?.type,
        value: values?.value,

        ...(values?.unit !== undefined
          ? { unit: values.unit }
          : {}),

        ...(values?.measuredAt
          ? { measuredAt: values.measuredAt }
          : {}),

        // ✅ Important pour les cartes OTHER
        ...(values?.customLabel !== undefined
          ? { customLabel: values.customLabel }
          : {}),
      };


      const { data } = await axiosMedical.post(
        ENDPOINTS.MEDICAL.VITALS(userId),
        body,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      return {
        data: unwrap(data),
        userId,
        clientId,
      };
    } catch (err) {
      return rejectWithValue({
        message: err?.response?.data || err?.message || "addVitalSignal failed",
        clientId,
      });
    }
  }
);

/**
 * UPDATE
 * payload:
 * {
 *   userId,
 *   id,
 *   values: Partial<{ type, value, unit, measuredAt, customLabel }>
 * }
 */
export const updateVitalSignal = createAsyncThunk(
  "vital-signs/update",
  async ({ userId, id, values }, { rejectWithValue }) => {
    try {
      const { data } = await axiosMedical.put(
        ENDPOINTS.MEDICAL.UPDATE_VITAL(userId, id),
        values,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      return {
        data: unwrap(data),
        userId,
        id,
      };
    } catch (err) {
      return rejectWithValue(
        err?.response?.data || err?.message || "updateVitalSignal failed"
      );
    }
  }
);

/**
 * DELETE
 * payload: { userId, id }
 */
export const deleteVitalSignal = createAsyncThunk(
  "vital-signs/delete",
  async ({ userId, id }, { rejectWithValue }) => {
    try {
      await axiosMedical.delete(
        ENDPOINTS.MEDICAL.DELETE_VITAL(userId, id),
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      return {
        id,
        userId,
      };
    } catch (err) {
      return rejectWithValue(
        err?.response?.data || err?.message || "deleteVitalSignal failed"
      );
    }
  }
);

// ✅ Alias pour Overviews.jsx
// Comme ton Overviews importe removeVitalSignal,
// on l’exporte aussi avec ce nom.
export const removeVitalSignal = deleteVitalSignal;